import {gettextCatalog} from "angular-gettext";
import {IAttributes, IDirective, IDirectiveController, INgModelController, IScope} from "angular";
import {regNavn} from "../tjenester/tredjepartsTjenestenavn";
import {SøkRessurs} from "../tjenester/ressurser/SøkRessurs";
import {Kontekst} from "../tjenester/Kontekst";
import {Språklager} from "../tjenester/i18n/Spraaklager";
import _ from "lodash";
import {Debugger} from "debug";
import {UuidGenerator} from "../tjenester/UuidGenerator";
import {FeltEndretEventTriggerDirektiv} from "./feltEndretEventTrigger";
import {Konfigurasjon, Miljø} from "felles/infrastruktur/Konfigurasjon";
import {erIkkeNill} from "felles/stoettefunk/nillsjekk";

declare var CKEDITOR: any;
declare var window: any;

/* ckeditor laster selv css og bilde-ressurser, angir korrekt rotsti, se angular.json for hvor ckeditor-koden blir kopiert ved bygging.
MERK at window.CKEDITOR_BASEPATH MÅ settes før ckeditor-koden lastes!
Dette gjøres i index-<mobil|web>.html
Se https://ckeditor.com/docs/ckeditor4/latest/guide/dev_basepath.html */
require('./ckeditor.scss');
require('../../../lib/ckeditor/dist/ckeditor.js');
//inkluder språkfiler - må holdes i synk spåkvalg i ckeditor (defaultLanguage og language i ckEditorKonfigurasjon())
require('../../../lib/ckeditor/dist/lang/en.js');
require('../../../lib/ckeditor/dist/lang/nb.js');
//inkluder plugins - må holdes i synk med plugins i ckEditorKonfigurasjon()
require('../../../lib/ckeditor/dist/plugins/autotag/plugin.js');
require('../../../lib/ckeditor/dist/plugins/toggleformateringsknapper/plugin.js');
require('../../../lib/ckeditor/dist/plugins/clipboard/dialogs/paste.js');
require('../../../lib/ckeditor/dist/plugins/colordialog/dialogs/colordialog');
require('../../../lib/ckeditor/dist/plugins/dialog/dialogDefinition.js');
require('../../../lib/ckeditor/dist/plugins/link/dialogs/anchor.js');
require('../../../lib/ckeditor/dist/plugins/link/dialogs/link.js');
require('../../../lib/ckeditor/dist/plugins/pastetools/filter/common.js');
require('../../../lib/ckeditor/dist/plugins/pastetools/filter/image.js');
require('../../../lib/ckeditor/dist/plugins/pastefromword/filter/default.js');
require('../../../lib/ckeditor/dist/plugins/table/dialogs/table.js');
require('../../../lib/ckeditor/dist/plugins/tabletools/dialogs/tableCell.js');

const debug: Debugger = require('debug')('ckeditor-tag');

/**
 * Angular direktiv som oppretter ckeditor instans.
 *
 * Se se https://ckeditor.com/docs/ckeditor4/latest/api/index.html for ckeditor API.
 *
 * Ckeditor laster stiler og bilder direkte, derfor må disse være tilgjengelig for lasting.
 * Vhja grunt:copy kopieres css og bilder til dist katalog for inkludering i bygg.
 * Sti til disse ressursene settes i rot-html-fila vhja CKEDITOR_BASEPATH
 *
 * Ved oppdatering av ckeditor må plugins vi selv har skrevet kopieres fra
 * lederplan-klient/src/lib/ckeditor/sf-plugins til lederplan-klient/src/lib/ckeditor/dist/plugins
 *
 * CSS for innhold lastes fra lederplan-klient/src/lib/ckeditor/css/editorinnhold.css
 *
 * Vedr. lisen:
 * https://support.ckeditor.com/hc/en-us/articles/115002539665-Can-I-include-CKEditor-4-and-CKEditor-5-in-a-commercial-environment-
 *
 * You can modify, integrate and use the Open Source CKEditor 4 and the Open Source CKEditor 5 in your commercial websites and products for free as long as our source code and your modifications remain open and accessible to others under the same Open Source license terms.

 CKEditor 4 is licensed under the terms of any of the following licenses at your choice: GPL, LGPL and MPL.
 CKEditor 5 is licensed under the terms of GPL 2+.
 */
export function ckeditorForInjisering() {
    return [SøkRessurs.regNavn, regNavn.gettext.gettextCatalog, Kontekst.regNavn, Språklager.regNavn, UuidGenerator.regNavn, Konfigurasjon.regNavn, ckeditor];
}

export function ckeditor(søkRessurs: SøkRessurs, gettextCatalog: gettextCatalog, kontekst: Kontekst, språklager: Språklager, uuidGenerator: UuidGenerator, appKonfigurasjon:Konfigurasjon): IDirective {
    return {
        restrict: 'E',
        require: ['?ngModel', '^?mdInputContainer'],
        scope: {gruppeId: '<', registrerEditor: '&'},
        link: (scope, elem, attrs, ctrls) => link(scope, elem, attrs, ctrls)
    };

    function link(scope: IScope, elem: JQLite, attrs: IAttributes, ctrls: IDirectiveController) {
        const handler = new CkEditorHandler(søkRessurs, gettextCatalog, kontekst, språklager, uuidGenerator, appKonfigurasjon);
        handler.linkFunksjon(scope, elem, attrs, ctrls);
    }
}

class CkEditorHandler {

    private scope: IScope;
    private elem: JQLite;
    private attrs: IAttributes;
    private ctrls: IDirectiveController;

    private id: string;
    private editor;
    private konfigurasjon;
    private ngModel: INgModelController;
    private mdInputContainer;

    private skjulerFormateringsknapperRadNrTo = false;
    private erMobil = false;

    constructor(private søkRessurs: SøkRessurs, private gettextCatalog: gettextCatalog, private kontekst: Kontekst, private språklager: Språklager, private uuidGenerator: UuidGenerator, private appKonfigurasjon:Konfigurasjon) {
    }

    linkFunksjon(scope: IScope, elem: JQLite, attrs: IAttributes, ctrls: IDirectiveController) {
        this.scope = scope;
        this.elem = elem;
        this.attrs = attrs;
        this.ctrls = ctrls;

        this.id = this.uuidGenerator.generer();
        this.ngModel = ctrls[0];
        this.mdInputContainer = ctrls[1];

        if (!this.ngModel) return;

        this.initCkEditorKonfigurasjon();
        this.initEditor();

        this.håndterMdModelOppdatering();
        if (this.mdInputContainer) this.håndterMdInputContainer();
    }

    /** Se https://ckeditor.com/docs/ckeditor4/latest/api/CKEDITOR_config.html */
    initCkEditorKonfigurasjon() {
        //global konfigurasjon
        CKEDITOR.config.customConfig = false; //ikke last config.js
        CKEDITOR.config.stylesSet = false; //ikke last styles.js
        CKEDITOR.config.defaultLanguage = 'nb'; //default language

        // undertrykker her varsel om utdatert ckeditor, ckeditor4 skal på sikt erstattes av ckeditor5 eller annen editor
        CKEDITOR.config.versionCheck = false;

        this.konfigurasjon = {
            //autotag pluginet blir lagt til via leggTilAutotagkonfigurasjon()
            plugins: 'toggleformateringsknapper,autocomplete,autogrow,basicstyles,clipboard,colorbutton,colordialog,emoji,enterkey,entities,font,indentblock,indentlist,justify,link,list,liststyle,pagebreak,pastefromword,pastetext,removeformat,selectall,showborders,table,tableresize,tableselection,textmatch,toolbar,undo,wysiwygarea',
            skin: 'minimalist',
            language: this.kontekst.autentisertBruker().spraak, //todo hente språk fra språklager?

            //konfig av knapper og knappegrupper
            //For toolbar konfig åpne i nettleser lederplan-klient/src/lib/ckeditor/dist/samples/toolbarconfigurator/index.html
            toolbar: [
                {name: 'basis', items: ['Bold', 'Italic', 'Underline']},
                {name: 'lister', items: ['NumberedList', 'BulletedList']},
                {name: 'lenker', items: ['Link', 'Unlink']},
                {name: 'paragraf', items: ['Outdent', 'Indent', 'ToggleFormateringsknapperKnapp']},
                '/', //sørger for ny rad
                {name: 'justering', items: ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock']},
                {name: 'insert', items: ['Table', 'PageBreak', 'EmojiPanel']},
                {name: 'diverse', items: ['SelectAll', 'RemoveFormat']},
                {name: 'styles', items: ['Font', 'FontSize', 'TextColor']}
            ],

            // simplified Dialog windows
            removeDialogTabs: 'link:advanced',

            //laster vår egen content stiling
            contentsCss: [CKEDITOR.getUrl('../css/editorinnhold.css')],
            bodyClass: 'ckeditor-innhold',

            //fokus til editor feltet når komponenten tegnes ut - overstyres av autofocus attr
            startupFocus: false,

            //tillater attributtene som autotag pluginet benytter
            //se https://ckeditor.com/docs/ckeditor4/latest/guide/dev_allowed_content_rules.html for syntaks
            extraAllowedContent: 'a[data-*](ckeditor-autotag*)',

            //merk at store bokstaver i font-navnene etter '/' ser ut til å ødelegge for at font-menyen reflekterer font brukt i tekst der markør står
            //benytter her samme visningsnavn som ble brukt ui SP2, men benytter åpent tilgjengelige fonter som substitutt for de lisensbelagte
            //må holdes i synk med lederplan-tjenester/src/main/resources/no/systemfabrikken/lederplan/tjenester/tekstmal/maler/generertepdfdokumenter/includes/fonter.scss
            font_names: "Courier/'Courier Prime', 'Courier Prime', Courier, monospace;" +
                "Helvetica/Inter, Helvetica, sans-serif;" +
                "Times Roman/'EB Garamond', 'Times New Roman', serif",

            fontSize_sizes: '6pt/6pt;8pt/8pt;9pt/9pt;10pt (standard)/10pt;11pt/11pt;12pt/12pt;14pt/14pt;16pt/16pt;18pt/18pt;20pt/20pt;22pt/22pt;24pt/24pt;26pt/26pt;28pt/28pt;36pt/36pt;48pt/48pt;72pt/72pt'
        };

        if (this.attrs.autofocus != undefined) this.konfigurasjon.startupFocus = 'end';
        //attr.placeholder støttes ikke (vi brukte dette på textarea - men støttes ikke av cdkeditor)
        //attr.escKey - bør implementeres - brukes i mobil app for å lukke modal

        //legger til klasse som sørger for at horisontal plass begrenses til hva som er tilgjengelig i gnerert PDF-dokument (via våre rapportmaler)
        if (this.attrs['innholdBrukesIPdf'] === 'true')
            this.konfigurasjon.bodyClass = this.konfigurasjon.bodyClass + ' ckeditor-innhold-pdf';

        this.erMobil = this.attrs.meny === 'mobil';
        if (this.erMobil) this.leggTilMobilkonfigurasjon();
        else this.leggTilWebkonfigurasjon();

        if (this.attrs.gruppeId != undefined) this.leggTilAutotagkonfigurasjon();
    }

    leggTilWebkonfigurasjon() {
        this.konfigurasjon.bodyClass = this.konfigurasjon.bodyClass + ' ckeditor-innhold-web';

        //se https://ckeditor.com/docs/ckeditor4/latest/guide/dev_autogrow.html
        const antallRader = _.isNumber(this.attrs.rows) ? parseInt(<any>this.attrs.rows) : 1;
        this.konfigurasjon.extraPlugins = 'autogrow, autolink';
        this.konfigurasjon.height = 25 * antallRader; //overstyrer height, ser ut til at høyden uansett ikke blir mindre enn 40 - overstyres av rows attr
        //this.konfigurasjon.autoGrow_bottomSpace = 40; //dette motvirker at vertikal scrollbar vises i et flash når ny linje legges til og editor må vokse - men det skaper også problemer med vertikal-aligning av strek under inputfelt med øvrige input-felter
        this.konfigurasjon.autoGrow_minHeight = 25 * antallRader; //overstyrer height, ser ut til at høyden uansett ikke blir mindre enn 40 - overstyres av rows attr
        this.konfigurasjon.autoGrow_maxHeight = 600;
        this.konfigurasjon.autoGrow_onStartup = true;
    }

    leggTilMobilkonfigurasjon() {
        this.konfigurasjon.bodyClass = this.konfigurasjon.bodyClass + ' ckeditor-innhold-mobil';

        this.konfigurasjon.removeButtons = this.konfigurasjon.removeButtons + ',Link,Unlink';
        this.konfigurasjon.height = this.elem.height() - 60;
    }

    /** aktiver autotag pluginet samt nødvendig konfigurasjon */
    leggTilAutotagkonfigurasjon() {
        this.konfigurasjon.plugins = this.konfigurasjon.plugins + ',autotag';
        this.konfigurasjon.autotag_config = new AutoTagKonfigurasjon(() => this.scope['gruppeId'], this.søkRessurs, this.gettextCatalog, this.kontekst).lesKonfigurasjon()
    }

    initEditor() {
        debug('opprettEditor(%s)', this.id)
        this.elem[0].innerHTML = `<div class="ng-ckeditor" id="ckeditor_${this.id}"></div>`;
        const elemEditor = this.elem[0].querySelectorAll('.ng-ckeditor');
        this.editor = CKEDITOR.appendTo(elemEditor[0], this.konfigurasjon, '');

        //sørg for å reg kommando som kreves av vårt lederplan-klient/src/lib/ckeditor/sf-plugins/toggleformateringsknapper plugin
        this.editor.addCommand('ToggleFormateringsknapperKommando', {
            exec: () => this.scope.$apply(() => {
                this.toggleVisAlleFormateringsknapper()
            })
        });

        //kjør kode som skal kjøres når cdkeditor er klar
        this.editor.on('instanceReady', () => this.scope.$apply(() => {
            if (this.attrs.registrerEditor)
                this.scope['registrerEditor']({editor: this.editor});
            this.skjulFormateringsknapperRadNrTo();
        }));

        //sørg for å tydde opp når scope termineres
        this.scope.$on('$destroy', () => {

            // ckeditor på ios får ikke ryddet opp eventlyttere skikkelig (ser det ut til)
            // dette fører til at tastaturet trigges igjen og igjen etter at dialog med ckeditor er lukket.
            // Ved å klone body-elementet som har lyttere på seg forsvinner disse,
            // og ved å erstatte body med sin egen klone unngår vi at ckeditor sin destroy-metode som rydder opp iframen
            // og mer til bryter sammen.

            if (this.appKonfigurasjon.klientTypeErMobil()) {
                const ckEditorIframe = this.elem[0].querySelector('iframe')
                const body = ckEditorIframe.contentDocument.querySelector('body');
                body.replaceWith(body.cloneNode(true));
            }

            this.editor.destroy();
        })
    }

    håndterMdModelOppdatering() {
        const oppdaterModell = (event) => {
            //bruker $evalAsync fremfor $apply da $apply gir '$digest already in progress' feil i mobilappen ved opprettelse av nytt møte
            this.scope.$evalAsync(() => {
                //debug("oppdaterModell(%s): %s, %O", this.id, this.editor.getData(), e)
                this.ngModel.$setViewValue(this.editor.getData())
            });
        }

        const setEditorData = (data) => {
            //debug("setEditorData(%s): %s", this.id, data)
            //setData() fører til at angitte data blir vasket - blir verdien "en tekst" gjort om til "<p>en tekst</p>"
            //i slike tilfeller blir change-eventet og dermed oppdaterModell blir kalt like etter at setData() har blitt kalt
            //Dermed oppdateres ngModellen fra den opprinnelige tilstanden selvom bruker ikke har gjort noen
            //eksplisitte endringer.
            this.editor.setData(data)
        }

        //change betinger undo pluginet, ref https://github.com/ckeditor/ckeditor-dev/issues/1364 (verifisert av kai juni 2023)
        this.editor.on('change', oppdaterModell);
        this.editor.on('dataReady', oppdaterModell);

        /* ser ut til at å sette innhold initielt vi ngModel.$render er tilstrekkelig
        ck.on('instanceReady', () => {
            console.log('CKED instanceReady');
            setEditorData(ngModel.$viewValue);
        });*/
        this.ngModel.$render = () => {
            setEditorData(this.ngModel.$viewValue);
        }
    }

    håndterMdInputContainer() {

        //Event fired as an indicator of the editor data loading.
        // It may be the result of calling setData explicitly or an internal editor ,
        // like the editor editing mode switching (move to Source and back).
        this.editor.on('dataReady', evt => this.mdInputContainer.setHasValue(!_.isEmpty(this.editor.getData())));

        //Fired when the editor instance receives the input focus.
        this.editor.on('focus', evt => this.mdInputContainer.setFocused(true));

        //Fired when the editor instance loses the input focus.
        this.editor.on('blur', evt => {
            this.mdInputContainer.setFocused(false);
            this.mdInputContainer.setHasValue(!_.isEmpty(this.editor.getData()));
            this.scope.$emit(FeltEndretEventTriggerDirektiv.eventer.feltendret);
        });

        //Event fired when this editor instance is destroyed.
        // The editor at this point is not usable and this event should be used to perform the clean-up in any plugin.
        this.editor.on('destroy', evt => {
            this.mdInputContainer.setFocused(false);
            this.mdInputContainer.setHasValue(false);
        });
    }

    toggleVisAlleFormateringsknapper() {
        if (this.skjulerFormateringsknapperRadNrTo)
            this.visFormateringsknapperRadNrTo();
        else
            this.skjulFormateringsknapperRadNrTo();
    }

    skjulFormateringsknapperRadNrTo() {
        this.skjulerFormateringsknapperRadNrTo = true;
        var toolbars: any[] = this.editor.toolbox.toolbars;
        toolbars.slice(this.erMobil ? 3 : 4).forEach(knappegruppe =>
            this.elem.find(`#${knappegruppe.id}`)
                .hide());
        this.elem.removeClass('ckeditor_viser_alle_formateringsknapper');
    }

    visFormateringsknapperRadNrTo() {
        this.skjulerFormateringsknapperRadNrTo = false;
        var toolbars: any[] = this.editor.toolbox.toolbars;
        toolbars.forEach(knappegruppe =>
            this.elem.find(`#${knappegruppe.id}`)
                .show());

        //skjul siste knappegruppe dersom skjermen er for smal
        const minKnapperadbredde = 580;
        if (this.elem.width() < minKnapperadbredde) {
            this.elem.find(`#${_.last(toolbars).id}`)
                .hide();
        }

        //legg til css-klasse som indikerer at utøket formateringsmeny vises
        this.elem.addClass('ckeditor_viser_alle_formateringsknapper');
    }
}

export enum AutoTagType {
    sak, dokument
}

/**
 * Klass for generering av autotag konfigurasjon
 */
class AutoTagKonfigurasjon {

    /** dersom søkestreng som ikke slutter på blankt kan * appendes for prefix søk */
    static kvalifisererTilPrefixSøk = /\S$/;

    constructor(private gruppeId: () => string, private søkRessurs: SøkRessurs, private gettextCatalog: gettextCatalog, private kontekst: Kontekst) {
    }

    private konfigurasjon = {
        typer: [
            this.sakKonfig(),
            this.dokumentKonfig()
        ],
        søk: (søkestreng, nøkkelord) => this.finnEntiteter(søkestreng, nøkkelord),
        språk: this.kontekst.autentisertBruker().spraak
    };

    lesKonfigurasjon() {
        return this.konfigurasjon;
    }

    private sakKonfig() {
        return {
            type: AutoTagType.sak,
            entitetType: 0,
            nøkkelord: {
                nb: ['sak', 's',],
                en: ['issue', 'i']
            },
            ///prefix som vises i forkant av lenke til sak når lenken er lagt til med autotag i ckeditor (redigering av html-felter)
            typenavn: this.gettextCatalog.getString('sak'),
            visningsnavn: hit => hit._source.nummer + ' ' + hit._source.tittel + ' (' + hit._source.motetittel + ' ' + hit._source.motenummer + ')',
            url: hit => '/app/innhold/moete/' + hit._source.møteId + '/sak/' + hit._id
        };
    }

    private dokumentKonfig() {
        return {
            type: AutoTagType.dokument,
            entitetType: 1, // dette er SøkEntitetType.dokument
            nøkkelord: {
                nb: ['dokument', 'd',],
                en: ['document', 'd']
            },
            ///prefix som vises i forkant av lenke til sak når lenken er lagt til med autotag i ckeditor (redigering av html-felter)
            typenavn: this.gettextCatalog.getString('dokument'),
            visningsnavn: hit => hit._source.filnavn,
            url: hit => '/app/innhold/dokument/' + hit._id
        };
    }

    private finnEntiteter(nøkkelord: string, søkestreng: string) {
        const konfigurasjon = this.finnNøkkelordKonfigurasjon(nøkkelord)

        return this.søkRessurs.finnEntiteter({
            sokestreng: this.forberedPrefixSøkestreng(søkestreng),
            entitetType: konfigurasjon.entitetType,
            gruppeId: this.gruppeId()
        }).$promise
            .then(resultat => {
                return _(resultat.hits.hits)
                    .map(treff => ({
                        id: treff._id,
                        type: AutoTagType[konfigurasjon.type],
                        typenavn: konfigurasjon.typenavn,
                        visningsnavn: konfigurasjon.visningsnavn(treff),
                        url: konfigurasjon.url(treff)
                    }))
                    .value();
            })
    }

    private finnNøkkelordKonfigurasjon(nøkkelord) {
        nøkkelord = nøkkelord.toLowerCase();
        var språk = this.konfigurasjon.språk;
        return _(this.konfigurasjon.typer)
            .find(v => _(v.nøkkelord[språk])
                .some(n => n === nøkkelord));
    }

    /** sørger for å legge til prefix søk (*) for siste ord i søkestreng */
    private forberedPrefixSøkestreng(søkestreng) {
        if (!AutoTagKonfigurasjon.kvalifisererTilPrefixSøk.exec(søkestreng)) return søkestreng;
        return søkestreng + '*';
    }
}


/*
ansvarlig: "odd@systemfabrikken.no"
beskrivelse: "en saksbeskrivelse"
dato: Mon Nov 26 2007 11:00:00 GMT+0100 (sentraleuropeisk normaltid) {constructor: ƒ}
gruppe: "b3dabc14-726a-41f4-93ad-e79ecff001f1"
gruppetittel: "Ledergruppe"
id: "b6ad72d7-5900-11e4-8ed6-0800200c9a66"
motenummer: "1"
motetittel: "Ledermøte"
møteId: "b6ad72d0-5900-11e4-8ed6-0800200c9a66"
nummer: "1"
sakstype: "Beslutning"
tema: "Økonomi"
tittel: "Agenda"
                    */

/*
dataid: "5347cc34-152a-4ea6-b11a-6472960db1d4"
dato: Fri Oct 19 2018 19:40:13 GMT+0200 (sentraleuropeisk sommertid) {constructor: ƒ}
filnavn: "budsjett.xlsx"
filnavn_fritekst: "budsjett.xlsx"
gruppe: "b3dabc14-726a-41f4-93ad-e79ecff001f1"
id: "41917a6b-cff6-4e5e-9804-4812d5090556"
innhold: "Ark1↵	et	regneark↵	1	2↵↵↵"
originalformat: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
pdfFilnavn: ""
pdfdataid: ""
 */


